import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { WEAPONS } from '../constants/gameSettings.js';

export default class Weapon {
    constructor(scene, bus, type = 'machineGun', audioManager, onReady = null) {
        this.scene = scene;
        this.bus = bus; // Assuming bus object has { mesh: THREE.Object3D, height: number, length: number }
        this.type = type;
        console.log(`[Weapon ${type}] Constructor received audioManager:`, audioManager); 
        this.audioManager = audioManager;
        this.onReadyCallback = onReady; // Callback when weapon is loaded and mounted

        console.log(`[Weapon] Creating new weapon of type: ${type}`);

        // Get weapon settings from constants
        this.settings = WEAPONS[this.type] || WEAPONS.machineGun;

        // Weapon properties
        this.damage = this.settings.damage;
        this.fireRate = this.settings.fireRate; // shots per second
        this.fireInterval = 1000 / this.fireRate; // Time between shots in ms
        this.projectileSpeed = this.settings.projectileSpeed;
        this.projectileSize = this.settings.projectileSize || 0.8;
        this.projectileType = this.settings.projectileType || 'bullet';
        this.soundId = this.settings.soundId; // ID of the sound to attach
        this.ammo = this.settings.ammo;
        this.maxAmmo = this.settings.ammo;
        this.reloadTime = this.settings.reloadTime * 1000; // Convert seconds to ms
        this.range = this.settings.range;
        this.spread = this.settings.spread; // Note: Spread is not yet implemented in fire()

        // State
        this.isReady = false;         // Flag: Is the mesh loaded and mounted?
        this.isFiring = false;        // Flag: Currently processing a shot?
        this.lastFireTime = 0;        // Timestamp of the last shot
        this.isReloading = false;     // Flag: Currently reloading?
        this.reloadStartTime = 0;     // Timestamp when reload started

        // Mesh and Mount Point
        this.mesh = null;             // The visual representation of the weapon
        this.mountPoint = null;       // An Object3D used to attach weapon to bus

        // Projectiles array
        this.projectiles = [];

        // Start loading the mesh asynchronously
        this.loadAndInitializeMesh();
    }

    async loadAndInitializeMesh() {
        try {
            const gltf = await this.loadWeaponModel();
            this.mesh = gltf.scene;
            this.prepareMesh(this.mesh); // Apply common mesh settings
            this.mountOnBus(); // Attach to the bus

            // Check if mounting was successful
            if (this.mesh && this.mountPoint && this.mountPoint.parent === this.bus.mesh) {
                this.isReady = true; // Weapon is ready to use!
                if (this.onReadyCallback) {
                    this.onReadyCallback(this); // Notify external code if needed
                }
                // --- Attach Sound Here ---
                if (this.audioManager && this.soundId && this.mesh) {
                    try {
                        // Use the weapon mesh itself as the sound source
                        this.audioManager.attachSound(this.soundId, this.mesh);
                        console.log(`[Weapon] Attached sound '${this.soundId}' to ${this.type} mesh.`);
                    } catch (error) {
                        console.error(`[Weapon] Failed to attach sound '${this.soundId}' to ${this.type}:`, error);
                    }
                } else if (this.soundId) {
                    console.warn(`[Weapon] Cannot attach sound '${this.soundId}': AudioManager not available or mesh missing.`);
                }
                // --- End Sound Attachment ---

                if (this.onReadyCallback) {
                    this.onReadyCallback(this);
                }

            } else {
                console.error(`[Weapon] ${this.type} mesh loaded but failed to mount correctly.`);
                // this.isReady remains false
            }

        } catch (error) {
            console.error(`[Weapon] Failed to create or mount weapon mesh for type ${this.type}:`, error);
            // Fallback to simple geometry if loading fails
            console.log('[Weapon] Falling back to placeholder geometry.');
            const geometry = new THREE.BoxGeometry(0.5, 0.5, 1.5); // Slightly larger fallback
            const material = new THREE.MeshStandardMaterial({ color: 0x555555 });
            this.mesh = new THREE.Mesh(geometry, material);
            this.prepareMesh(this.mesh);
            this.mountOnBus(); // Attempt to mount the fallback

            // Check fallback mount success
            if (this.mesh && this.mountPoint && this.mountPoint.parent === this.bus.mesh) {
                this.isReady = true;
                console.log(`[Weapon] ${this.type} (fallback) is ready and mounted.`);


                // --- Attach Sound Here (Fallback) ---
                if (this.audioManager && this.soundId && this.mesh) {
                    try {
                        this.audioManager.attachSound(this.soundId, this.mesh);
                        console.log(`[Weapon] Attached sound '${this.soundId}' to ${this.type} (fallback) mesh.`);
                    } catch (error) {
                        console.error(`[Weapon] Failed to attach sound '${this.soundId}' to ${this.type} (fallback):`, error);
                    }
                } else if (this.soundId) {
                    console.warn(`[Weapon] Cannot attach sound '${this.soundId}' (fallback): AudioManager not available or mesh missing.`);
                }
                // --- End Sound Attachment (Fallback) ---
                if (this.onReadyCallback) {
                    this.onReadyCallback(this);
                }
            } else {
                console.error(`[Weapon] ${this.type} (fallback) failed to mount correctly.`);
            }
        }
    }

    async loadWeaponModel() {
        // You might want different models per weapon type later
        let modelPath = '/models/weapon/Turret.glb';
        // if (this.type === 'rocketLauncher') modelPath = '/models/weapon/RocketLauncher.glb';
        // ... etc.

        const loader = new GLTFLoader();
        console.log(`[Weapon] Loading model from: ${modelPath}`);
        return await loader.loadAsync(modelPath);
    }

    prepareMesh(mesh) {
        // Apply common settings AFTER loading/creating the mesh
        mesh.traverse((node) => {
            node.visible = true; // Ensure visibility
            if (node.isMesh) {
                node.castShadow = true;
                node.receiveShadow = true;
                node.frustumCulled = false; // Consider performance impact

                if (node.material) {
                    // Ensure basic material properties for visibility
                    const material = node.material;
                    material.transparent = false;
                    material.opacity = 1.0;
                    material.depthTest = true;
                    material.depthWrite = true;
                    // Enhance appearance (optional)
                    material.metalness = material.metalness !== undefined ? Math.max(0.6, material.metalness) : 0.6;
                    material.roughness = material.roughness !== undefined ? Math.min(0.4, material.roughness) : 0.4;
                    material.needsUpdate = true;
                }
            }
        });
    }

    mountOnBus() {
        console.log(`[Weapon] Attempting to mount ${this.type} on bus`);

        if (!this.mesh) {
            console.error("[Weapon] Cannot mount: Weapon mesh is not loaded.");
            return;
        }
        if (!this.bus?.mesh) { // Optional chaining for safety
            console.warn(`[Weapon] Cannot mount weapon: bus or bus mesh is missing`);
            return;
        }
        // Basic check for bus dimensions - replace with actual properties if named differently
        if (this.bus.height === undefined || this.bus.length === undefined) {
            console.warn(`[Weapon] Bus dimensions (height/length) are undefined. Using estimated values.`);
            // Provide safe defaults or stop if dimensions are critical
            this.bus.height = this.bus.height ?? 4; // Example default height
            this.bus.length = this.bus.length ?? 10; // Example default length
        }


        // Standardize using a mount point Object3D
        this.mountPoint = new THREE.Object3D();
        this.mountPoint.name = `${this.type}_mountPoint`;

        // --- Define Mount & Weapon Transform Defaults ---
        // Position of the mount point relative to the bus origin
        let mountPosition = new THREE.Vector3(0, this.bus.height * 0.8, -this.bus.length * 0.3);
        // Scale of the weapon model *within* the mount point
        let weaponScale = new THREE.Vector3(1, 1, 1);
        // Rotation of the weapon model *within* the mount point (Euler angles)
        let weaponRotation = new THREE.Euler(0, Math.PI, 0); // Point forward along bus's -Z
        // Position offset of the weapon model relative *to* the mount point origin
        let weaponPositionOffset = new THREE.Vector3(0, 0, 0);

        // --- Customize per Weapon Type ---
        switch (this.type) {
            case 'machineGun':
                mountPosition.set(0, this.bus.height + 53, -this.bus.length * 0.25); // Higher up
                weaponScale.set(35.0, 35.0, 35.0); // Turret model seems to need large scale
                // weaponRotation default is fine (0, PI, 0)
                // weaponPositionOffset default is fine (0, 0, 0)
                break;

            case 'rocketLauncher':
                mountPosition.set(0, this.bus.height * 0.9, 0); // Top middle
                // weaponScale.set(x, y, z); // Adjust if rocket launcher model needs different scale
                // weaponRotation.set(rx, ry, rz); // Adjust if needed
                // weaponPositionOffset.set(px, py, pz); // Adjust if model origin isn't centered
                break;

            // Add other weapon types here...

            default:
                console.warn(`[Weapon] Using default mount settings for unknown type: ${this.type}`);
            // Defaults defined above will be used
        }

        // Apply transforms to the weapon mesh itself (relative to mount point)
        this.mesh.scale.copy(weaponScale);
        this.mesh.rotation.copy(weaponRotation);
        this.mesh.position.copy(weaponPositionOffset);

        // Add the weapon mesh to the mount point
        this.mountPoint.add(this.mesh);

        // Position the mount point on the bus
        this.mountPoint.position.copy(mountPosition);

        // --- Add Mount Point to Bus ---
        this.bus.mesh.add(this.mountPoint);

        // --- Optional: Add Axes Helper for Debugging ---
        // const axesHelper = new THREE.AxesHelper(50); // Adjust size as needed
        // this.mesh.add(axesHelper); // Add to weapon mesh to see its local axes (red=X, green=Y, blue=Z)
        // Blue (Z) should point out the barrel if rotation is (0, PI, 0)

        console.log(`[Weapon] ${this.type} mounted on '${this.mountPoint.name}' at bus local pos:`, mountPosition.toArray().map(n => n.toFixed(2)));
        console.log(`[Weapon] Mesh parent: ${this.mesh.parent?.name}, Mount parent: ${this.mountPoint.parent?.name}`);
    }


    fire() {
        console.log(`[Weapon ${this.type}] fire() method entered.`);
        // --- Pre-fire Checks ---
        if (!this.isReady) { console.log(`[Weapon ${this.type}] Returning: Not ready.`); return; }
        if (this.isFiring) { console.log(`[Weapon ${this.type}] Returning: Already firing.`); return; }
        if (this.isReloading) { console.log(`[Weapon ${this.type}] Returning: Reloading.`); return; }
        const now = performance.now();
        if (now - this.lastFireTime < this.fireInterval) { console.log(`[Weapon ${this.type}] Returning: Fire rate limit.`); return; }
        if (this.ammo <= 0) { console.log(`[Weapon ${this.type}] Returning: Out of ammo.`); return; }

        console.log(this.audioManager)
        console.log(this.soundId)
        // --- Play Sound ---
        // Play the sound associated with this weapon, if available
        if (this.audioManager && this.soundId) {
            // Use the simple play method - it will play the sound attached in loadAndInitializeMesh
            console.log(this.soundId)
            this.audioManager.play(this.soundId);
        } else if (this.soundId) {
            // Only warn if a soundId was expected but audioManager is missing
            console.warn(`[Weapon] Cannot play sound: AudioManager not available for sound ID ${this.soundId}`);
        }
        // --- End Play Sound ---


        // --- Fire! ---
        // console.log('[Weapon] Fire!');
        this.isFiring = true; // Prevent re-entry and rate issues
        this.lastFireTime = now;
        this.ammo--;

        // console.log(`Ammo remaining: ${this.ammo}`);

        // --- Create Projectile ---
        // Use a slightly larger size for easier initial debugging if needed

        let projectileMesh;
        let projectileGeometry = new THREE.SphereGeometry(0.8, 8, 8);
        let projectileMaterial = new THREE.MeshStandardMaterial({
            color: 0xffaa00, // Bright orange/yellow
            emissive: 0xffaa00,
            emissiveIntensity: 1.0
        });
        const projectile = new THREE.Mesh(projectileGeometry, projectileMaterial);
        projectile.castShadow = true;
        projectile.name = `projectile_${this.type}_${Date.now()}`;

        // --- Calculate World Position and Direction ---
        const worldPosition = new THREE.Vector3();
        const worldQuaternion = new THREE.Quaternion();

        // Ensure matrices are up-to-date before getting world transforms
        // Crucial if the bus (and thus the mount point) moved this frame
        this.mountPoint.updateMatrixWorld(true); // Update parent first
        this.mesh.getWorldPosition(worldPosition); // Get world position of weapon's origin
        this.mesh.getWorldQuaternion(worldQuaternion); // Get world orientation of weapon

        // --- Muzzle Position ---
        // Define muzzle offset relative to the weapon's LOCAL coordinate system
        // (0,0,0 = weapon origin, Z is usually forward/backward)
        // Adjust this vector based on your model's origin and barrel location
        const muzzleOffsetLocal = new THREE.Vector3(0, 1.7, -2.5); // Example: Slightly up and forward from origin
        const muzzlePosition = worldPosition.clone().add(
            muzzleOffsetLocal.clone().applyQuaternion(worldQuaternion) // Rotate offset to world space and add
        );
        projectile.position.copy(muzzlePosition);

        // --- Direction ---
        // Base direction is negative Z axis in local space (common convention)
        const direction = new THREE.Vector3(0, 0, 1);
        // Apply the weapon's world rotation to the direction vector
        direction.applyQuaternion(worldQuaternion);
        direction.normalize(); // Ensure it's a unit vector


        // TODO: Implement Spread/Inaccuracy
        // if (this.spread > 0) {
        //     const spreadAngle = THREE.MathUtils.randFloat(-this.spread, this.spread) * Math.PI / 180;
        //     const spreadAxis = new THREE.Vector3(Math.random() - 0.5, Math.random() - 0.5, Math.random() - 0.5).normalize();
        //     direction.applyAxisAngle(spreadAxis, spreadAngle);
        // }
        if (this.projectileType === 'laser') {
            // --- Laser Beam ---
            const laserLength = 10; // Visual length of the laser pulse
            projectileGeometry = new THREE.CylinderGeometry(
                this.projectileSize, // Radius top (use setting)
                this.projectileSize, // Radius bottom (use setting)
                laserLength,         // Length of the cylinder
                8,                   // Radial segments (lower for performance)
                1,                   // Height segments
                false                // Open ended (doesn't matter much here)
            );

            projectileMaterial = new THREE.MeshStandardMaterial({
                color: 0xff0000, // Bright red laser
                emissive: 0xff0000, // Use MeshBasicMaterial OR emissive on Standard
                emissiveIntensity: 1.5,
                metalness: 0.0,
                roughness: 0.8,
                //transparent: true,
                //opacity: 0.8,
            });

            projectileMesh = new THREE.Mesh(projectileGeometry, projectileMaterial);
            projectileMesh.castShadow = false; // Lasers don't typically cast shadows
            projectileMesh.name = `projectile_laser_${Date.now()}`;

            // --- Align Cylinder with Direction using Quaternion ---

            // 1. Define the cylinder's default "length" axis (it's created along Y)
            const cylinderUpAxis = new THREE.Vector3(0, 1, 0);

            // 2. Create a quaternion that represents the rotation from the cylinder's default up axis
            //    to the desired world firing 'direction'.
            const quaternion = new THREE.Quaternion();
            quaternion.setFromUnitVectors(cylinderUpAxis, direction); // Magic happens here!

            // 3. Apply this rotation to the projectile mesh
            projectileMesh.quaternion.copy(quaternion);

            // 4. Position the mesh: Start with the center at the muzzle
            projectileMesh.position.copy(muzzlePosition);

            // 5. Offset the mesh forward by half its length ALONG the firing direction
            //    so the *base* of the laser appears at the muzzle.
            const offset = direction.clone().multiplyScalar(laserLength / 2);
            projectileMesh.position.add(offset);


        } else {
            // --- Default Bullet ---
            projectileGeometry = new THREE.SphereGeometry(
                this.projectileSize, // Use the size setting
                8,                   // Width segments
                8                    // Height segments
            );
            projectileMaterial = new THREE.MeshStandardMaterial({
                color: 0xffaa00,
                emissive: 0xffaa00,
                emissiveIntensity: 1.0
            });

            projectileMesh = new THREE.Mesh(projectileGeometry, projectileMaterial);
            projectileMesh.castShadow = true;
            projectileMesh.name = `projectile_${this.type}_${Date.now()}`;

            // Position the sphere at the muzzle
            projectileMesh.position.copy(muzzlePosition);
        }


        // --- Add to Scene and Track ---
        this.scene.add(projectileMesh);
        this.projectiles.push({
            mesh: projectileMesh, // Store the created mesh
            direction: direction,
            spawnTime: now,
            distance: 0
        });

        // --- Reset Firing State ---
        this.isFiring = false; // Allow next shot attempt after cooldown
        // console.log('[Weapon] Fire complete!');
    }

    reload() {
        if (this.isReloading || this.ammo === this.maxAmmo || !this.isReady) {
            return; // Already reloading, full ammo, or weapon not ready
        }
        console.log(`[Weapon] ${this.type} starting reload (${(this.reloadTime / 1000).toFixed(1)}s)...`);
        this.isReloading = true;
        this.reloadStartTime = performance.now();
        // Optional: Dispatch reload event, play sound, show UI indicator
        // this.bus.dispatchEvent({ type: 'weaponReloadStart', weaponType: this.type });
    }

    update(deltaTime) {
        // Ignore update if weapon isn't ready
        if (!this.isReady) return;

        // Basic sanity check for deltaTime
        if (!deltaTime || deltaTime <= 0 || deltaTime > 0.5) {
            // console.warn(`[Weapon] Suspicious deltaTime: ${deltaTime}`);
            deltaTime = 1 / 60; // Fallback to ~60fps delta if needed
        }

        const now = performance.now();

        // --- Update Projectiles ---
        for (let i = this.projectiles.length - 1; i >= 0; i--) {
            const proj = this.projectiles[i];

            // Move projectile
            const movement = proj.direction.clone().multiplyScalar(this.projectileSpeed * deltaTime);
            proj.mesh.position.add(movement);
            proj.distance += movement.length();

            // --- Projectile Removal Logic ---
            let remove = false;
            if (proj.distance > this.range) {
                remove = true; // Exceeded range
            }

            // Optional: Add max lifetime check (e.g., 10 seconds)
            const maxLifetime = 10000; // ms
            if (now - proj.spawnTime > maxLifetime) {
                remove = true; // Exceeded lifetime
            }

            // Check for collisions with floating cubes
            const projBox = new THREE.Box3().setFromObject(proj.mesh);
            const cubes = this.scene.children.filter(obj => obj.userData?.floatingCube);

            for (const cube of cubes) {
                const cubeBox = new THREE.Box3().setFromObject(cube);
                if (projBox.intersectsBox(cubeBox)) {
                    cube.userData.floatingCube.hit();
                    remove = true;
                    break;
                }
            }

            // Check for collisions with enemies
            if (!remove) {
                const enemies = this.scene.children.filter(obj => obj.userData?.enemy);
                for (const enemyMesh of enemies) {
                    const enemyBox = new THREE.Box3().setFromObject(enemyMesh);
                    if (projBox.intersectsBox(enemyBox)) {
                        enemyMesh.userData.enemy.hit(this.damage);
                        remove = true;
                        break;
                    }
                }
            }

            if (remove) {
                this.scene.remove(proj.mesh);
                // Consider disposing geometry/material if performance becomes an issue
                // proj.mesh.geometry.dispose();
                // proj.mesh.material.dispose();
                this.projectiles.splice(i, 1);
            }
        }

        // --- Handle Reloading ---
        if (this.isReloading) {
            if (now - this.reloadStartTime >= this.reloadTime) {
                this.ammo = this.maxAmmo;
                this.isReloading = false;
                console.log(`[Weapon] ${this.type} reload complete! Ammo: ${this.ammo}`);
                // Optional: Dispatch reload complete event
                // this.bus.dispatchEvent({ type: 'weaponReloadEnd', weaponType: this.type });
            } else {
                // Optional: Update reload progress UI
                // const progress = (now - this.reloadStartTime) / this.reloadTime;
            }
        }
    }

    // Helper to get ammo status
    getAmmoStatus() {
        return {
            current: this.ammo,
            max: this.maxAmmo,
            isReloading: this.isReloading,
        };
    }

    // Clean up when weapon is destroyed or replaced
    dispose() {
        console.log(`[Weapon] Disposing ${this.type}`);
        // Remove all projectiles
        this.projectiles.forEach(proj => {
            this.scene.remove(proj.mesh);
            // proj.mesh.geometry?.dispose(); // Optional cleanup
            // proj.mesh.material?.dispose(); // Optional cleanup
        });
        this.projectiles = [];

        // Remove mesh from mount point
        if (this.mesh) {
            this.mountPoint?.remove(this.mesh); // Remove from mount point first
            // Optionally dispose mesh resources if it won't be reused
            // this.mesh.traverse(node => { /* dispose geometry/material */ });
        }
        // Remove mount point from bus
        if (this.mountPoint) {
            this.bus?.mesh?.remove(this.mountPoint); // Remove mount point from bus
        }

        this.mesh = null;
        this.mountPoint = null;
        this.isReady = false;
        this.audioManager = null;
        // Potentially remove event listeners if any were added
    }
}